import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export const DocH2 = ({ children, id }: { children: ReactNode; id?: string }) => (
  <h2
    id={id}
    className="scroll-mt-24 text-[22px] font-semibold tracking-tight text-[#101828] sm:text-[24px]"
  >
    {children}
  </h2>
);

export const DocH3 = ({ children }: { children: ReactNode }) => (
  <h3 className="text-[15px] font-semibold text-[#101828]">{children}</h3>
);

export const DocP = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("text-[15px] leading-[1.7] text-[#525866]", className)}>{children}</p>
);

export const DocCode = ({ children }: { children: ReactNode }) => (
  <code className="rounded bg-[#F9FBFF] px-1.5 py-0.5 font-mono text-[12.5px] text-[#101828]">
    {children}
  </code>
);

export const DocList = ({ items }: { items: ReactNode[] }) => (
  <ul className="flex flex-col gap-2">
    {items.map((item, index) => (
      <li key={index} className="flex gap-3 text-[14.5px] leading-[1.65] text-[#525866]">
        <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-[#335CFF]" aria-hidden />
        <span className="min-w-0">{item}</span>
      </li>
    ))}
  </ul>
);

export const DocTable = ({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) => (
  <div className="w-full overflow-x-auto rounded-xl border border-[#EFF2FF]">
    <table className="w-full min-w-[480px] border-collapse text-left text-[13px]">
      <thead>
        <tr className="bg-[#F9FBFF] text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
          {headers.map((header) => (
            <th key={header} className="px-4 py-2.5 font-semibold">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} className="border-t border-[#EFF2FF] align-top">
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  "px-4 py-3 leading-[1.6] text-[#525866]",
                  cellIndex === 0 && "whitespace-nowrap font-medium text-[#101828]",
                )}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DocSection = ({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-4">
    <DocH2 id={id}>{title}</DocH2>
    {children}
  </section>
);
